import { useState, useCallback, useRef, useMemo, useEffect } from 'react'
import type { CardOption } from './cardListTypes'

interface UseCardSelectionProps {
  options: Array<CardOption>
  selection?: Array<string>
  disabled: boolean
  select: 'single' | 'multiple'
  onSelectionChange: (selectedTitles: Array<string>) => void
  onAction?: (action: string) => boolean | void
}

interface UseCardSelectionReturn {
  selectedIndices: Set<number>
  optionClickHandlers: Array<() => void>
}

export function useCardSelection({
  options,
  selection,
  disabled,
  select,
  onSelectionChange,
  onAction,
}: UseCardSelectionProps): UseCardSelectionReturn {
  // Use indices for internal selection tracking to handle duplicate titles
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(() => {
    // Initialize from selection prop by finding matching indices
    const initialIndices = new Set<number>()
    if (selection) {
      selection.forEach(title => {
        const index = options.findIndex(opt => opt.title === title)
        if (index !== -1) {
          initialIndices.add(index)
        }
      })
    }
    return initialIndices
  })

  const onSelectionChangeRef = useRef(onSelectionChange)
  onSelectionChangeRef.current = onSelectionChange
  const onActionRef = useRef(onAction)
  onActionRef.current = onAction

  const isInternalUpdateRef = useRef(false)
  const prevSelectionKeyRef = useRef<string | undefined>(undefined)

  const validTitles = useMemo(
    () => new Set(options.map(opt => opt.title)),
    [options],
  )

  const selectionKey = useMemo(() => selection?.join('\0'), [selection])

  useEffect(() => {
    const prevSelectionKey = prevSelectionKeyRef.current
    const willSync = (
      selection !== undefined &&
      !disabled &&
      !isInternalUpdateRef.current &&
      prevSelectionKey !== selectionKey
    )
    if (willSync) {
      const newIndices = new Set<number>()
      const usedIndices = new Set<number>()
      selection.forEach(title => {
        const index = options.findIndex((opt, idx) => opt.title === title && !usedIndices.has(idx))
        if (index !== -1) {
          newIndices.add(index)
          usedIndices.add(index)
        }
      })

      const currentArray = Array.from(selectedIndices)
      const hasChanges =
        newIndices.size !== currentArray.length ||
        currentArray.some(idx => !newIndices.has(idx))

      if (hasChanges) {
        setSelectedIndices(newIndices)
      }
    }
    prevSelectionKeyRef.current = selectionKey
    isInternalUpdateRef.current = false
  }, [selection, selectionKey, disabled, selectedIndices, options])

  const handleOptionClick = useCallback((index: number) => {
    if (disabled) {
      return
    }

    const clickedOption = options[index]
    if (clickedOption?.action && onActionRef.current?.(clickedOption.action)) {
      return
    }

    isInternalUpdateRef.current = true

    setSelectedIndices(prevSelected => {
      const newSelection = new Set(prevSelected)

      if (select === 'single') {
        if (newSelection.has(index)) {
          newSelection.delete(index)
        } else {
          newSelection.clear()
          newSelection.add(index)
        }
      } else {
        if (newSelection.has(index)) {
          newSelection.delete(index)
        } else {
          newSelection.add(index)
        }
      }

      // Convert indices back to titles for the callback
      const selectedTitles = Array.from(newSelection).map(idx => options[idx]?.title).filter(Boolean)

      const otherSelections = (selection ?? []).filter(s => !validTitles.has(s))
      const mergedSelection = [...otherSelections, ...selectedTitles]

      onSelectionChangeRef.current(mergedSelection)
      return newSelection
    })
  }, [disabled, select, selection, validTitles, options])

  const optionClickHandlers = useMemo(() =>
    options.map((_, index) => () => handleOptionClick(index)),
  [options, handleOptionClick])

  return { selectedIndices, optionClickHandlers }
}
